import type { LiceReportResult } from '@/routes/Client/LiceReport/types';
import { getIsoWeek, getIsoWeekYear } from '@/utils/date';

/** A week as the selector's pills consume it. */
export interface LiceReportWeekOption {
    weekNumber: number;
    year: number;
    interimReport: boolean;
    /** Matches the key `buildLiceReportSelectionMap` files each weekly report under. */
    key: string;
}

/**
 * The weeks the selector offers, newest first, in the order the reports arrive.
 *
 * Shared because both report pages had their own identical copy, each defined inside the
 * component and called inline in JSX — so the array had a new identity on every render, which
 * is what kept re-triggering the panel's collapse and losing the reader's selection.
 */
export function toLiceReportWeekOptions(
    reports: Array<Pick<LiceReportResult, 'isoWeek' | 'isoYear' | 'interimReport'>>
): LiceReportWeekOption[] {
    return reports.map((report) => ({
        weekNumber: report.isoWeek,
        year: report.isoYear,
        interimReport: report.interimReport,
        key: `${report.isoWeek}-${report.isoYear}`,
    }));
}

/**
 * Whether a weekly report covers the week we are currently in, and is therefore still being
 * added to.
 *
 * Both pages derived "this week" with a hand-rolled calculation —
 * `Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)` over a local-time `Date` —
 * and compared it against `report.isoWeek`, which *is* ISO. The two disagree around a year
 * boundary, so a report could be labelled an interim report in the wrong week. The same
 * mismatch was fixed on the backend in `e21f5d30`.
 */
export function isInterimLiceReportWeek(report: { isoWeek: number; isoYear: number }): boolean {
    const today = new Date();

    return report.isoWeek === getIsoWeek(today) && report.isoYear === getIsoWeekYear(today);
}
